/**
 * i18n setup — react-i18next over HTTP backend.
 *
 * Loads `/locales/{lng}/common.json` lazily from `public/locales/...`.
 * Locale resolution order:
 *   1. `kidzio.locale` in localStorage (the language switcher writes this).
 *   2. `<html lang>` if set.
 *   3. Browser's `navigator.language` (and its prefix).
 *   4. Falls back to `en`.
 *
 * Backend (LLM) gets the locale via the chat request so Kio answers in the
 * user's language — see `dashboard/AskKio.tsx`.
 */
import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LANGUAGES = ['en', 'ru', 'hy', 'es', 'pl'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<SupportedLanguage, { native: string; english: string; flag: string }> = {
  en: { native: 'English',   english: 'English',   flag: '🇬🇧' },
  ru: { native: 'Русский',   english: 'Russian',   flag: '🇷🇺' },
  hy: { native: 'Հայերեն',   english: 'Armenian',  flag: '🇦🇲' },
  es: { native: 'Español',   english: 'Spanish',   flag: '🇪🇸' },
  pl: { native: 'Polski',    english: 'Polish',    flag: '🇵🇱' },
};

void i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    nonExplicitSupportedLngs: true, // 'en-US' resolves to 'en'
    defaultNS: 'common',
    ns: ['common'],
    interpolation: { escapeValue: false },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      // Prefer the explicit user choice; fall back to browser hints.
      order: ['localStorage', 'htmlTag', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'kidzio.locale',
    },
    returnNull: false,
  });

export { i18n };

/**
 * Save the user's choice + apply it. Keeps localStorage and the i18next
 * runtime in sync.
 */
export async function setLanguage(lng: SupportedLanguage): Promise<void> {
  await i18n.changeLanguage(lng);
}
